import React, { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataProvider, useData } from "./context/DataContext";

import LoginPage from "./pages/LoginPage";
import Sidebar   from "./components/Sidebar";

import AdminDashboard  from "./pages/admin/AdminDashboard";
import AllTasksAdmin   from "./pages/admin/AllTasksAdmin";
import PendingTasksAdmin from "./pages/admin/PendingTasksAdmin";
import CompletedTasksAdmin from "./pages/admin/CompletedTasksAdmin";
import OverdueTasksAdmin from "./pages/admin/OverdueTasksAdmin";
import MonitorAdmin    from "./pages/admin/MonitorAdmin";
import UsersPage       from "./pages/admin/UsersPage";
import CreateTaskPage  from "./pages/admin/CreateTaskPage";

import EmployeeDashboard  from "./pages/employee/EmployeeDashboard";
import AllTasksEmployee   from "./pages/employee/AllTasksEmployee";
import PendingTasks       from "./pages/employee/PendingTasks";
import CompletedTasks     from "./pages/employee/CompletedTasks";
import OverdueTasks       from "./pages/employee/OverdueTasks";
import CreatedTasks       from "./pages/employee/CreatedTasks";
import MonitorEmployee    from "./pages/employee/MonitorEmployee";
import CreateTaskEmployee from "./pages/employee/CreateTaskEmployee";

const PAGE_LABELS = {
  dashboard: "Dashboard", "all-tasks": "All Tasks",
  monitor: "Monitor", "create-task": "Create Task",
  users: "Employees", pending: "Pending Tasks",
  completed: "Completed Tasks", overdue: "Overdue Tasks",
  created: "Created Tasks",
};

function TopBar({ activePage, onRefresh, onMenuClick }) {
  const { user, logout }          = useAuth();
  const { loading, lastFetched }  = useData();

  return (
    <div className="topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button className="hamburger" onClick={onMenuClick}>☰</button>
        <span style={{ fontSize: 13, color: "var(--gray-400)", textTransform: "capitalize" }}>{user?.role}</span>
        <span style={{ color: "var(--gray-300)" }}>/</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-800)" }}>
          {PAGE_LABELS[activePage] || activePage}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {lastFetched && (
          <span style={{ fontSize: 11, color: "var(--gray-400)" }}>Updated {lastFetched}</span>
        )}

        <div style={{
          width: 34, height: 34, borderRadius: "50%",
          background: user?.role === "admin" ? "var(--primary)" : "#059669",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "white", fontSize: 12, fontWeight: 700,
          overflow: "hidden",
        }}>
          {user?.avatar
            ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : (user?.initials || user?.name?.[0]?.toUpperCase())
          }
        </div>
      </div>
    </div>
  );
}

function AppShell() {
  const { user, loading: authLoading } = useAuth();
  const { refresh, loading }           = useData();
  const [activePage, setActivePage]    = useState("dashboard");
  const [sidebarOpen, setSidebarOpen]  = useState(false);

  useEffect(() => { if (user) refresh(); }, [user]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <div className="spinner" style={{ margin: "0 auto 12px" }} />
          <p style={{ color: "var(--gray-400)", fontSize: 14 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const isAdmin = user.role === "admin";

  const renderPage = () => {
    if (isAdmin) {
      switch (activePage) {
        case "dashboard":    return <AdminDashboard onNavigate={setActivePage} />;
        case "all-tasks":   return <AllTasksAdmin />;
        case "pending":     return <PendingTasksAdmin />;
        case "completed":   return <CompletedTasksAdmin />;
        case "overdue":     return <OverdueTasksAdmin />;
        case "monitor":     return <MonitorAdmin />;
        case "users":       return <UsersPage />;
        case "create-task": return <CreateTaskPage />;
        default:            return <AdminDashboard onNavigate={setActivePage} />;
      }
    } else {
      switch (activePage) {
        case "dashboard":   return <EmployeeDashboard onNavigate={setActivePage} />;
        case "all-tasks":   return <AllTasksEmployee />;
        case "pending":     return <PendingTasks />;
        case "completed":   return <CompletedTasks />;
        case "overdue":     return <OverdueTasks />;
        case "created":     return <CreatedTasks />;
        case "monitor":     return <MonitorEmployee />;
        case "create-task": return <CreateTaskEmployee />;
        default:            return <EmployeeDashboard onNavigate={setActivePage} />;
      }
    }
  };

  return (
    <div className="layout">
      <Sidebar activePage={activePage} onNavigate={setActivePage} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        <TopBar activePage={activePage} onRefresh={refresh} onMenuClick={() => setSidebarOpen(o => !o)} />
        <div className="page-content">{renderPage()}</div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <AppShell />
      </DataProvider>
    </AuthProvider>
  );
}
